import { useState, useRef, useEffect } from "react";
import emailjs from "@emailjs/browser";

export default function ContactForm() {
  const formRef = useRef();
  const [isMobile, setIsMobile] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const services = [
    "Web Development",
    "Mobile App Development",
    "AI & ML Solutions",
    "UI/UX Design",
    "Cloud & DevOps",
    "E-Commerce",
    "Other",
  ];

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError("");

    if (name === "user_name") {
      setForm((prev) => ({
        ...prev,
        name: value,
      }));
    } else if (name === "user_email") {
      setForm((prev) => ({
        ...prev,
        email: value,
      }));
    } else if (name === "user_phone") {
      setForm((prev) => ({
        ...prev,
        phone: value,
      }));
    } else if (name === "message") {
      setForm((prev) => ({
        ...prev,
        message: value,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      setError("Please fill in all fields");
      return;
    }

    if (form.message.length < 10) {
      setError("Message should be at least 10 characters long");
      return;
    }

    setLoading(true);

    emailjs
      .sendForm(
        "service_rk34ocf",
        "template_y2cgkxh",
        formRef.current,
        {
          publicKey: "ofLxSuVq5gKWNWErL",
        }
      )
      .then(
        (result) => {
          console.log("SUCCESS:", result.text);
          setLoading(false);
          setSubmitted(true);
          setForm({
            name: "",
            email: "",
            phone: "",
            service: "",
            message: "",
          });

          // Reset after 4 seconds
          setTimeout(() => {
            setSubmitted(false);
          }, 4000);
        },
        (error) => {
          console.log("FULL EMAILJS ERROR:", error);
          setLoading(false);
          setError(error?.text || "Failed to send message. Please try again.");
        }
      );
  };

  const getInputIcon = (name) => {
    const icons = {
      name: "👤",
      email: "✉️",
      phone: "📱",
      service: "⚙️",
      message: "💬",
    };
    return icons[name] || "";
  };

  const inputStyle = (name) => ({
    width: "100%",
    padding: "16px 16px 16px 50px",
    borderRadius: 16,
    fontSize: 14.5,
    border: `2px solid ${focused === name ? "#3B82F6" : "#E0E7FF"}`,
    background: focused === name 
      ? "linear-gradient(135deg, #F0F6FF 0%, #FAFBFF 100%)"
      : "linear-gradient(135deg, #FAFBFF 0%, #F5F7FF 100%)",
    color: "#0F172A",
    outline: "none",
    fontFamily: "'Sora', sans-serif",
    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
    boxShadow: focused === name
      ? "0 12px 32px rgba(59, 130, 246, 0.15), inset 0 1px 3px rgba(255,255,255,0.9)"
      : "0 6px 20px rgba(30, 58, 138, 0.08), inset 0 1px 2px rgba(255,255,255,0.6)",
    position: "relative",
  });

  const styles = {
    section: {
      padding: isMobile ? "60px 20px" : "96px 40px",
      background: "linear-gradient(160deg, #F8FAFC 0%, #EFF6FF 100%)",
      position: "relative",
      overflow: "hidden",
      minHeight: "100vh",
    },
    decorativeCircle1: {
      position: "absolute",
      top: isMobile ? -100 : -80,
      right: isMobile ? -100 : -60,
      width: isMobile ? 250 : 400,
      height: isMobile ? 250 : 400,
      borderRadius: "50%",
      background:
        "radial-gradient(circle, rgba(59, 130, 246, 0.08), transparent 70%)",
      pointerEvents: "none",
    },
    decorativeCircle2: {
      position: "absolute",
      bottom: isMobile ? -80 : -60,
      left: isMobile ? -80 : -40,
      width: isMobile ? 250 : 350,
      height: isMobile ? 250 : 350,
      borderRadius: "50%",
      background:
        "radial-gradient(circle, rgba(16, 185, 129, 0.07), transparent 70%)",
      pointerEvents: "none",
    },
    container: {
      maxWidth: 1200,
      margin: "0 auto",
    },
    header: {
      textAlign: "center",
      marginBottom: isMobile ? 40 : 60,
    },
    badge: {
      display: "inline-block",
      padding: "6px 14px",
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 600,
      background: "#3B82F618",
      color: "#3B82F6",
      border: "1px solid #3B82F633",
      letterSpacing: ".04em",
      textTransform: "uppercase",
      marginBottom: 16,
    },
    heading: {
      fontFamily: "'Sora', sans-serif",
      fontSize: isMobile ? "28px" : "clamp(28px, 4vw, 46px)",
      fontWeight: 800,
      color: "#0F172A",
      margin: "16px 0 14px",
      lineHeight: 1.2,
    },
    subheading: {
      color: "#6B7280",
      maxWidth: 520,
      margin: "0 auto",
      fontSize: isMobile ? 15 : 16,
      lineHeight: 1.75,
    },
    contactGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: isMobile ? 32 : 40,
      alignItems: isMobile ? "auto" : "stretch",
    },
    infoPanel: {
      background: "linear-gradient(135deg, #1E3A8A, #2563EB, #3B82F6)",
      position: "relative",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      borderRadius: 28,
      padding: isMobile ? 28 : 42,
      color: "#fff",
      boxShadow: "0 20px 50px rgba(30, 58, 138, 0.2)",
    },
    formPanel: {
      background: "#fff",
      borderRadius: 28,
      padding: isMobile ? 24 : 40,
      boxShadow: "0 12px 40px rgba(15, 23, 42, 0.08)",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: 16,
      marginBottom: 16,
    },
    successContainer: {
      textAlign: "center",
      padding: isMobile ? "30px 0" : "40px 0",
      animation: "fadeIn 0.5s ease-in",
    },
    successCircle: {
      width: 88,
      height: 88,
      borderRadius: "50%",
      background: "linear-gradient(135deg, #10B981, #059669)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 24px",
      boxShadow: "0 12px 36px rgba(16, 185, 129, 0.35)",
      fontSize: 40,
      color: "#fff",
      animation: "popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
    },
    button: {
      width: "100%",
      padding: isMobile ? "14px" : "15px",
      borderRadius: 12,
      background: loading
        ? "#93C5FD"
        : "linear-gradient(135deg, #1E3A8A, #3B82F6)",
      color: "#fff",
      border: "none",
      cursor: loading ? "not-allowed" : "pointer",
      fontWeight: 800,
      fontSize: isMobile ? 15 : 16,
      fontFamily: "'Sora', sans-serif",
      transition: "all 0.3s ease",
      boxShadow: loading
        ? "none"
        : "0 10px 25px rgba(59, 130, 246, 0.3)",
      transform: loading ? "scale(1)" : "scale(1)",
    },
    errorMsg: {
      background: "#FEE2E2",
      color: "#DC2626",
      padding: "12px 16px",
      borderRadius: 12,
      fontSize: 14,
      marginBottom: 16,
      border: "1px solid #FCA5A5",
      animation: "slideDown 0.3s ease",
    },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes popIn {
          0% {
            transform: scale(0.5);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes slideDown {
          from {
            transform: translateY(-10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes shimmer {
          0%, 100% {
            background-position: -1000px 0;
          }
          50% {
            background-position: 1000px 0;
          }
        }

        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        input[type=number] {
          -moz-appearance: textfield;
        }

        textarea::-webkit-scrollbar {
          width: 6px;
        }

        textarea::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        textarea::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #3B82F6, #60A5FA);
          border-radius: 10px;
        }

        textarea::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #1E40AF, #3B82F6);
        }

        input::placeholder,
        textarea::placeholder {
          color: #CBD5E1;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          input,
          select,
          textarea {
            font-size: 16px !important;
          }
        }
      `}</style>

      <section id="contact" style={styles.section}>
        <div style={styles.decorativeCircle1} />
        <div style={styles.decorativeCircle2} />

        <div style={styles.container}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.badge}>✨ Get in Touch</div>
            <h2 style={styles.heading}>Let's Build Something Amazing</h2>
            <p style={styles.subheading}>
              Got a project in mind? Tell us about it and our team will get back
              to you within 24 hours. We're excited to hear from you!
            </p>
          </div>

          {/* Main Grid */}
          <div style={styles.contactGrid}>
            {/* LEFT INFO PANEL */}
            <div style={styles.infoPanel}>
              <div
                style={{
                  position: "absolute",
                  top: -40,
                  right: -40,
                  width: 250,
                  height: 250,
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.06)",
                  zIndex: 0,
                }}
              />

              <div style={{ position: "relative", zIndex: 1 }}>
                <h3
                  style={{
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 800,
                    fontSize: isMobile ? 22 : 26,
                    marginBottom: 12,
                    lineHeight: 1.3,
                  }}
                >
                  Get in Touch
                </h3>

                <p
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: isMobile ? 13.5 : 14.5,
                    lineHeight: 1.7,
                    marginBottom: 36,
                  }}
                >
                  We're here to help you transform your ideas into world-class
                  digital products. Reach out and let's collaborate!
                </p>

                {/* Contact Info */}
                <div style={{ marginBottom: 36 }}>
                  {[
                    {
                      icon: "📞",
                      label: "Phone",
                      val: "+91 96462 06032",
                      href: "tel:+919646206032",
                    },
                    {
                      icon: "✉️",
                      label: "Email",
                      val: "info@techvanto.com",
                      href: "mailto:info@techvanto.com",
                    },
                    {
                      icon: "📍",
                      label: "Location",
                      val: "New Delhi, India 🇮🇳",
                    },
                    {
                      icon: "⏰",
                      label: "Working Hours",
                      val: "Mon–Sat, 9AM – 7PM IST",
                    },
                  ].map((item, idx) => (
                    <div
                      key={item.label}
                      style={{
                        display: "flex",
                        gap: 14,
                        marginBottom: 20,
                        alignItems: "flex-start",
                        animation: `slideUp 0.5s ease ${idx * 0.1}s both`,
                      }}
                    >
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: 10,
                          background: "rgba(255, 255, 255, 0.12)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 18,
                          flexShrink: 0,
                          backdropFilter: "blur(10px)",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {item.icon}
                      </div>

                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "rgba(255, 255, 255, 0.6)",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            marginBottom: 3,
                          }}
                        >
                          {item.label}
                        </div>

                        {item.href ? (
                          <a
                            href={item.href}
                            style={{
                              fontSize: 14.5,
                              color: "#fff",
                              fontWeight: 600,
                              textDecoration: "none",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.opacity = "0.8";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.opacity = "1";
                            }}
                          >
                            {item.val}
                          </a>
                        ) : (
                          <div
                            style={{
                              fontSize: 14.5,
                              color: "#fff",
                              fontWeight: 600,
                            }}
                          >
                            {item.val}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    height: 1,
                    background: "rgba(255, 255, 255, 0.15)",
                    margin: "36px 0",
                  }}
                />

                {/* Why Work With Us */}
                <h4
                  style={{
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 700,
                    fontSize: isMobile ? 16 : 17,
                    marginBottom: 20,
                  }}
                >
                  Why Work With Us?
                </h4>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                  }}
                >
                  {[
                    ["⚡", "Response in under 24 hours"],
                    ["🔒", "NDA signing available"],
                    ["🎯", "Free project estimation"],
                    ["🤝", "Dedicated project manager"],
                    ["💳", "Flexible engagement models"],
                  ].map(([icon, text], idx) => (
                    <div
                      key={text}
                      style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "center",
                        padding: "10px 0",
                        animation: `slideUp 0.5s ease ${0.5 + idx * 0.1}s both`,
                      }}
                    >
                      <span style={{ fontSize: 18 }}>{icon}</span>
                      <span
                        style={{
                          fontSize: 14,
                          color: "rgba(255, 255, 255, 0.85)",
                          fontWeight: 500,
                        }}
                      >
                        {text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT FORM PANEL */}
            <div style={styles.formPanel}>
              {submitted ? (
                <div style={styles.successContainer}>
                  <div style={styles.successCircle}>✓</div>

                  <h3
                    style={{
                      fontFamily: "'Sora', sans-serif",
                      fontSize: isMobile ? 22 : 24,
                      fontWeight: 800,
                      color: "#0F172A",
                      marginBottom: 12,
                    }}
                  >
                    Message Sent! 🎉
                  </h3>

                  <p
                    style={{
                      color: "#6B7280",
                      fontSize: 15,
                      lineHeight: 1.7,
                      maxWidth: 340,
                      margin: "0 auto 28px",
                    }}
                  >
                    Thank you for reaching out! Our team will review your
                    message and contact you within 24 hours.
                  </p>

                  <div
                    style={{
                      display: "inline-block",
                      padding: "8px 16px",
                      borderRadius: 8,
                      background: "#DCFCE7",
                      color: "#166534",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    ✓ Submission successful
                  </div>
                </div>
              ) : (
                <>
                  <h3
                    style={{
                      fontFamily: "'Sora', sans-serif",
                      fontWeight: 800,
                      fontSize: isMobile ? 20 : 22,
                      color: "#0F172A",
                      marginBottom: 6,
                    }}
                  >
                    Send Us a Message
                  </h3>

                  <p
                    style={{
                      color: "#9CA3AF",
                      fontSize: 13.5,
                      marginBottom: 24,
                    }}
                  >
                    Fill out the form below and we'll respond as soon as
                    possible.
                  </p>

                  {error && <div style={styles.errorMsg}>{error}</div>}

                  <form ref={formRef} onSubmit={handleSubmit}>
                    {/* Name & Email Row */}
                    <div style={styles.formGrid}>
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#1E293B",
                            marginBottom: 8,
                            textTransform: "uppercase",
                            letterSpacing: ".05em",
                          }}
                        >
                          Full Name *
                        </label>
                        <div style={{ position: "relative" }}>
                          <span
                            style={{
                              position: "absolute",
                              left: 16,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: 18,
                              pointerEvents: "none",
                            }}
                          >
                            {getInputIcon("name")}
                          </span>
                          <input
                            type="text"
                            name="user_name"
                            value={form.name}
                            onChange={handleChange}
                            onFocus={() => setFocused("name")}
                            onBlur={() => setFocused(null)}
                            placeholder="John Doe"
                            required
                            style={inputStyle("name")}
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#1E293B",
                            marginBottom: 8,
                            textTransform: "uppercase",
                            letterSpacing: ".05em",
                          }}
                        >
                          Email *
                        </label>
                        <div style={{ position: "relative" }}>
                          <span
                            style={{
                              position: "absolute",
                              left: 16,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: 18,
                              pointerEvents: "none",
                            }}
                          >
                            {getInputIcon("email")}
                          </span>
                          <input
                            type="email"
                            name="user_email"
                            value={form.email}
                            onChange={handleChange}
                            onFocus={() => setFocused("email")}
                            onBlur={() => setFocused(null)}
                            placeholder="john@company.com"
                            required
                            style={inputStyle("email")}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Phone & Service Row */}
                    <div style={styles.formGrid}>
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#1E293B",
                            marginBottom: 8,
                            textTransform: "uppercase",
                            letterSpacing: ".05em",
                          }}
                        >
                          Phone Number *
                        </label>
                        <div style={{ position: "relative" }}>
                          <span
                            style={{
                              position: "absolute",
                              left: 16,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: 18,
                              pointerEvents: "none",
                            }}
                          >
                            {getInputIcon("phone")}
                          </span>
                          <input
                            type="tel"
                            name="user_phone"
                            value={form.phone}
                            onChange={handleChange}
                            onFocus={() => setFocused("phone")}
                            onBlur={() => setFocused(null)}
                            placeholder="+91 XXXXX XXXXX"
                            required
                            style={inputStyle("phone")}
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#1E293B",
                            marginBottom: 8,
                            textTransform: "uppercase",
                            letterSpacing: ".05em",
                          }}
                        >
                          Service Type *
                        </label>
                        <div style={{ position: "relative" }}>
                          <span
                            style={{
                              position: "absolute",
                              left: 16,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: 18,
                              pointerEvents: "none",
                              zIndex: 2,
                            }}
                          >
                            {getInputIcon("service")}
                          </span>
                          <select
                            name="service"
                            value={form.service}
                            onChange={handleChange}
                            onFocus={() => setFocused("service")}
                            onBlur={() => setFocused(null)}
                            required
                            style={{
                              ...inputStyle("service"),
                              cursor: "pointer",
                              appearance: "none",
                              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%233B82F6' d='M1 1l5 5 5-5'/%3E%3C/svg%3E")`,
                              backgroundRepeat: "no-repeat",
                              backgroundPosition: "right 14px center",
                              paddingRight: "40px",
                            }}
                          >
                            <option value="">Select a service…</option>
                            {services.map((service) => (
                              <option key={service} value={service}>
                                {service}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Message */}
                    <div style={{ marginBottom: 24 }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#1E293B",
                          marginBottom: 8,
                          textTransform: "uppercase",
                          letterSpacing: ".05em",
                        }}
                      >
                        Project Details *
                      </label>
                      <div style={{ position: "relative" }}>
                        <span
                          style={{
                            position: "absolute",
                            left: 16,
                            top: 16,
                            fontSize: 18,
                            pointerEvents: "none",
                          }}
                        >
                          {getInputIcon("message")}
                        </span>
                        <textarea
                          name="message"
                          value={form.message}
                          onChange={handleChange}
                          onFocus={() => setFocused("message")}
                          onBlur={() => setFocused(null)}
                          placeholder="Tell us about your project, goals, timeline, and budget..."
                          rows={5}
                          required
                          style={{
                            ...inputStyle("message"),
                            resize: "vertical",
                            minHeight: 140,
                            lineHeight: 1.7,
                            paddingLeft: 50,
                          }}
                        />
                      </div>

                      <div
                        style={{
                          textAlign: "right",
                          fontSize: 11,
                          color: "#9CA3AF",
                          marginTop: 6,
                          fontWeight: 500,
                        }}
                      >
                        {form.message.length} / 1000
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        ...styles.button,
                        opacity: loading ? 0.9 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 15px 35px rgba(59, 130, 246, 0.4)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading) {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow =
                            "0 10px 25px rgba(59, 130, 246, 0.3)";
                        }
                      }}
                    >
                      {loading ? (
                        <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                          <span
                            style={{
                              display: "inline-block",
                              width: 16,
                              height: 16,
                              border: "2px solid rgba(255,255,255,0.3)",
                              borderTopColor: "#fff",
                              borderRadius: "50%",
                              animation: "spin 0.8s linear infinite",
                            }}
                          />
                          Sending...
                        </span>
                      ) : (
                        "🚀 Send Message"
                      )}
                    </button>

                    {/* Additional Info */}
                    <p
                      style={{
                        fontSize: 12,
                        color: "#9CA3AF",
                        marginTop: 16,
                        textAlign: "center",
                      }}
                    >
                      We typically respond within 24 hours. Your privacy is
                      important to us.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
}